import React, { useState, useRef, useCallback } from 'react';
import { DndContext, useDraggable } from '@dnd-kit/core';

// ── Constants ────────────────────────────────────────────────────────────────
export const LANE_HEIGHT = 180;
export const CANVAS_HEIGHT = LANE_HEIGHT * 3; // 540
const INITIAL_CANVAS_WIDTH = 2400;
const NODE_SIZE = 32;

const TIME_BANDS = [
  { label: 'seconds', width: 40 },
  { label: 'minutes', width: 80 },
  { label: 'hours', width: 120 },
  { label: 'days', width: 180 },
  { label: 'weeks', width: 260 },
  { label: 'months', width: 360 },
  { label: 'years', width: 500 },
];

// 9 snap points — top / middle / bottom within each of the 3 lanes.
// pxY is the pixel Y of the snap target; y_position is stored in the DB.
// Spacing: snap at 1/6, 1/2, 5/6 of each lane.
const SNAP_POINTS = [
  { pxY: Math.round(LANE_HEIGHT * 0 + LANE_HEIGHT / 6),     y_position: 1.0  }, // top-lane  top
  { pxY: Math.round(LANE_HEIGHT * 0 + LANE_HEIGHT / 2),     y_position: 0.9  }, // top-lane  mid
  { pxY: Math.round(LANE_HEIGHT * 0 + LANE_HEIGHT * 5 / 6), y_position: 0.8  }, // top-lane  btm
  { pxY: Math.round(LANE_HEIGHT * 1 + LANE_HEIGHT / 6),     y_position: 0.6  }, // mid-lane  top
  { pxY: Math.round(LANE_HEIGHT * 1 + LANE_HEIGHT / 2),     y_position: 0.5  }, // mid-lane  mid
  { pxY: Math.round(LANE_HEIGHT * 1 + LANE_HEIGHT * 5 / 6), y_position: 0.4  }, // mid-lane  btm
  { pxY: Math.round(LANE_HEIGHT * 2 + LANE_HEIGHT / 6),     y_position: 0.2  }, // bot-lane  top
  { pxY: Math.round(LANE_HEIGHT * 2 + LANE_HEIGHT / 2),     y_position: 0.1  }, // bot-lane  mid
  { pxY: Math.round(LANE_HEIGHT * 2 + LANE_HEIGHT * 5 / 6), y_position: 0.0  }, // bot-lane  btm
];
// With LANE_HEIGHT=180: 30, 90, 150, 210, 270, 330, 390, 450, 510

// y_position float → nearest snap pixel
const yPosToPx = (yPos) =>
  SNAP_POINTS.reduce((best, s) =>
    Math.abs(s.y_position - yPos) < Math.abs(best.y_position - yPos) ? s : best
  ).pxY;

// raw pixel Y → nearest snap point
const pxToSnap = (px) =>
  SNAP_POINTS.reduce((best, s) =>
    Math.abs(s.pxY - px) < Math.abs(best.pxY - px) ? s : best
  );

const snapToTimeBand = (gapPx) => {
  if (gapPx <= 0) return TIME_BANDS[0];
  return TIME_BANDS.reduce((best, b) =>
    Math.abs(b.width - gapPx) < Math.abs(best.width - gapPx) ? b : best
  );
};

// ── Lane config ───────────────────────────────────────────────────────────────
// y_position used to match DB lane records (lanes store 1.0 / 0.5 / 0.0)
const LANES = [
  { y_position: 1.0, bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-700', defaultLabel: 'Exceeded expectations' },
  { y_position: 0.5, bg: 'bg-gray-50',    border: 'border-gray-100',    text: 'text-gray-400',    defaultLabel: 'Neutral'               },
  { y_position: 0.0, bg: 'bg-amber-50',   border: 'border-amber-100',   text: 'text-amber-700',   defaultLabel: 'Below expectations'    },
];

// ── Draggable milestone node ──────────────────────────────────────────────────
const MilestoneNode = ({ milestone, index, interactive, isLabeling, onStartLabel, onLabelSave }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: milestone.id,
    disabled: !interactive || isLabeling,
  });

  const yPx = yPosToPx(milestone.y_position);

  const wrapStyle = {
    position: 'absolute',
    left: milestone.x_position - NODE_SIZE / 2,
    top: yPx - NODE_SIZE / 2,
    width: NODE_SIZE,
    height: NODE_SIZE,
    touchAction: 'none',
    zIndex: isDragging ? 50 : 10,
    transform: transform ? `translate3d(${transform.x}px,${transform.y}px,0)` : undefined,
    cursor: interactive ? (isDragging ? 'grabbing' : 'grab') : 'default',
  };

  return (
    <div
      ref={setNodeRef}
      style={wrapStyle}
      onClick={(e) => e.stopPropagation()}
      {...(interactive && !isLabeling ? { ...listeners, ...attributes } : {})}
    >
      {/* Circle */}
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-black
          shadow-md border-2 border-white select-none transition-transform
          ${isDragging ? 'scale-110 bg-rutabaga-pink-hover' : 'bg-rutabaga-pink'}`}
      >
        {index + 1}
      </div>

      {/* Label — below the node */}
      <div
        className="absolute top-9 left-1/2 -translate-x-1/2 whitespace-nowrap"
        style={{ pointerEvents: 'auto' }}
      >
        {isLabeling ? (
          <input
            autoFocus
            className="text-xs border border-rutabaga-pink rounded px-1.5 py-0.5 outline-none text-center shadow-sm bg-white"
            style={{ minWidth: 80 }}
            defaultValue={milestone.label || ''}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') onLabelSave(milestone.id, e.target.value || 'Untitled');
              if (e.key === 'Escape') onLabelSave(milestone.id, milestone.label || 'Untitled');
            }}
            onBlur={(e) => onLabelSave(milestone.id, e.target.value || 'Untitled')}
          />
        ) : (
          <span
            className="text-[10px] font-bold text-rutabaga-dark bg-white px-1.5 py-0.5 rounded shadow-sm border border-gray-100"
            style={{ cursor: interactive ? 'text' : 'default' }}
            onDoubleClick={interactive ? () => onStartLabel(milestone.id) : undefined}
          >
            {milestone.label || '…'}
          </span>
        )}
      </div>
    </div>
  );
};

// ── Main canvas ───────────────────────────────────────────────────────────────
const JourneyMapCanvas = ({
  milestones = [],
  lanes = [],
  onAddMilestone,
  onMoveMilestone,
  onUpdateLabel,
  readonly = false,
}) => {
  const scrollRef = useRef(null);
  const [labelingId, setLabelingId] = useState(null);
  const [pending, setPending] = useState(null);

  // Infinite canvas: starts at INITIAL_CANVAS_WIDTH, grows in EXPANSION_STEP chunks
  const EXPANSION_STEP = 1200;
  const [canvasWidth, setCanvasWidth] = useState(INITIAL_CANVAS_WIDTH);
  const canvasWidthRef = useRef(INITIAL_CANVAS_WIDTH);

  const expandIfNeeded = useCallback((x) => {
    if (x >= canvasWidthRef.current * 0.5) {
      const next = canvasWidthRef.current + EXPANSION_STEP;
      canvasWidthRef.current = next;
      setCanvasWidth(next);
    }
  }, []);

  const sorted = [...milestones].sort((a, b) => a.x_position - b.x_position);

  // Match DB lane label by y_position, fall back to default
  const getLaneLabel = (yPos, fallback) =>
    lanes.find(l => Math.abs(l.y_position - yPos) < 0.1)?.label || fallback;

  // ── Canvas click: add milestone ──────────────────────────────────────────
  const handleCanvasClick = useCallback((e) => {
    if (readonly || pending) return;
    const rect = scrollRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + scrollRef.current.scrollLeft;
    const rawY = e.clientY - rect.top;

    const snap = pxToSnap(rawY);
    const prev = sorted.filter(m => m.x_position < x).at(-1);
    const timeBand = prev ? snapToTimeBand(x - prev.x_position).label : null;

    expandIfNeeded(x);
    setPending({ x_position: x, y_position: snap.y_position, time_from_previous: timeBand, prev_id: prev?.id || null });
    setLabelingId('pending');
  }, [readonly, pending, sorted, expandIfNeeded]);

  const confirmPendingLabel = useCallback((label) => {
    if (!pending) return;
    const finalLabel = label.trim();
    setLabelingId(null);
    setPending(null);
    if (finalLabel) onAddMilestone?.({ ...pending, label: finalLabel });
  }, [pending, onAddMilestone]);

  // ── dnd-kit drag end ─────────────────────────────────────────────────────
  const handleDragEnd = useCallback(({ active, delta }) => {
    const m = milestones.find(ms => ms.id === active.id);
    if (!m) return;

    const newX = Math.max(NODE_SIZE, m.x_position + delta.x);
    const rawYPx = yPosToPx(m.y_position) + delta.y;
    const clampedYPx = Math.max(SNAP_POINTS[0].pxY, Math.min(SNAP_POINTS[SNAP_POINTS.length - 1].pxY, rawYPx));
    const snap = pxToSnap(clampedYPx);

    const others = sorted.filter(ms => ms.id !== m.id);
    const prev = others.filter(ms => ms.x_position < newX).at(-1);
    const timeBand = prev ? snapToTimeBand(newX - prev.x_position).label : null;

    expandIfNeeded(newX);
    onMoveMilestone?.({ id: m.id, x_position: newX, y_position: snap.y_position, time_from_previous: timeBand });
  }, [milestones, sorted, onMoveMilestone, expandIfNeeded]);

  // ── SVG connector lines ───────────────────────────────────────────────────
  const lines = sorted.slice(1).map((m, i) => ({
    key: `${sorted[i].id}-${m.id}`,
    x1: sorted[i].x_position,
    y1: yPosToPx(sorted[i].y_position),
    x2: m.x_position,
    y2: yPosToPx(m.y_position),
    band: m.time_from_previous,
  }));

  return (
    <div className="flex select-none" style={{ height: CANVAS_HEIGHT }}>

      {/* ── Lane label sidebar ──────────────────────────────────────────── */}
      <div className="w-32 shrink-0 flex flex-col border-r border-gray-200 bg-white z-10">
        {LANES.map((lane, i) => (
          <div
            key={lane.y_position}
            className={`flex-1 flex flex-col pr-3 ${i < 2 ? 'border-b border-gray-100' : ''} ${lane.text}`}
            style={{
              justifyContent: i === 0 ? 'flex-start' : i === 1 ? 'center' : 'flex-end',
              paddingTop:    i === 0 ? 10 : 0,
              paddingBottom: i === 2 ? 10 : 0,
            }}
          >
            <span className="text-[9px] font-black uppercase tracking-widest text-right leading-tight">
              {getLaneLabel(lane.y_position, lane.defaultLabel)}
            </span>
          </div>
        ))}
      </div>

      {/* ── Scrollable canvas ────────────────────────────────────────────── */}
      <DndContext onDragEnd={handleDragEnd}>
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-auto overflow-y-hidden relative"
          style={{ height: CANVAS_HEIGHT, cursor: readonly ? 'default' : 'crosshair' }}
          onClick={handleCanvasClick}
        >
          <div className="relative" style={{ width: canvasWidth, height: CANVAS_HEIGHT }}>

            {/* Lane backgrounds */}
            {LANES.map((lane, i) => (
              <div
                key={lane.y_position}
                className={`absolute left-0 right-0 ${lane.bg} border-b ${lane.border}`}
                style={{ top: LANE_HEIGHT * i, height: LANE_HEIGHT }}
              />
            ))}

            {/* SVG: connector lines + time labels at line midpoint */}
            <svg
              className="absolute inset-0 pointer-events-none"
              style={{ width: canvasWidth, height: CANVAS_HEIGHT }}
            >
              {lines.map(l => {
                const mx = (l.x1 + l.x2) / 2;
                const my = (l.y1 + l.y2) / 2;
                return (
                  <g key={l.key}>
                    <line
                      x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
                      stroke="#E02F50" strokeWidth={2} strokeDasharray="5 4" strokeOpacity={0.35}
                    />
                    {l.band && (
                      <text
                        x={mx}
                        y={my - 10}
                        textAnchor="middle"
                        fontSize={9}
                        fontFamily="Roboto, sans-serif"
                        fontWeight="700"
                        fill="#E02F50"
                        opacity={0.6}
                      >
                        {l.band}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>

            {/* Real milestones */}
            {sorted.map((m, i) => (
              <MilestoneNode
                key={m.id}
                milestone={m}
                index={i}
                interactive={!readonly}
                isLabeling={labelingId === m.id}
                onStartLabel={setLabelingId}
                onLabelSave={(id, label) => {
                  setLabelingId(null);
                  if (label !== m.label) onUpdateLabel?.({ id, label });
                }}
              />
            ))}

            {/* Pending milestone (label input before DB write) */}
            {pending && (
              <MilestoneNode
                key="pending"
                milestone={{ id: 'pending', ...pending, label: '' }}
                index={sorted.length}
                interactive={false}
                isLabeling
                onStartLabel={() => {}}
                onLabelSave={(_, label) => confirmPendingLabel(label)}
              />
            )}

            {/* Empty state hint */}
            {!readonly && milestones.length === 0 && !pending && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-gray-300 font-black text-base tracking-tight">
                  Click anywhere to place your first milestone
                </p>
              </div>
            )}
          </div>
        </div>
      </DndContext>
    </div>
  );
};

export default JourneyMapCanvas;
